import type { Cell, Command, PlayerId, SimState, Unit } from '../sim';

/** One of the player's unit groups, with its stable number (Group 1, 2, …; see sim `formGroup`). */
export interface OwnGroup {
  id: string;
  number: number;
  leader: string;
  units: Unit[];
}

const idNumber = (id: string) => Number.parseInt(id.slice(1), 10);

/** The player's groups by number. Pure (no React). */
export function ownGroups(state: SimState, playerId: PlayerId): OwnGroup[] {
  const members = new Map<string, Unit[]>();
  for (const u of Object.values(state.units)) {
    if (u.owner !== playerId || u.groupId === undefined || !state.groups?.[u.groupId]) continue;
    const list = members.get(u.groupId);
    if (list) list.push(u);
    else members.set(u.groupId, [u]);
  }
  return [...members.keys()]
    .map((id) => ({
      id,
      number: state.groups?.[id]?.number ?? 0,
      leader: state.groups?.[id]?.leader ?? '',
      units: (members.get(id) ?? []).sort((a, b) => idNumber(a.id) - idNumber(b.id)),
    }))
    .sort((a, b) => a.number - b.number || idNumber(a.id) - idNumber(b.id));
}

/**
 * The `group` command for the selection (own units; the last selected
 * leads), or null when it has fewer than two or they already are one group.
 */
export function groupCommand(
  state: SimState,
  playerId: PlayerId,
  selected: readonly string[]
): Command | null {
  const own = selected.filter((id) => state.units[id]?.owner === playerId);
  if (own.length < 2) return null;
  const first = state.units[own[0]]?.groupId;
  const same = first !== undefined && own.every((id) => state.units[id]?.groupId === first);
  if (same && ownGroups(state, playerId).find((g) => g.id === first)?.units.length === own.length) {
    return null;
  }
  return { type: 'group', unitIds: own };
}

/** The `ungroup` command for the selection's grouped own units, or null when none is grouped. */
export function ungroupCommand(
  state: SimState,
  playerId: PlayerId,
  selected: readonly string[]
): Command | null {
  const grouped = selected.filter((id) => {
    const u = state.units[id];
    return u?.owner === playerId && u.groupId !== undefined;
  });
  return grouped.length > 0 ? { type: 'ungroup', unitIds: grouped } : null;
}

/** Ms within which a second press of a group's key, or click on its row, takes the camera there. */
export const GROUP_DOUBLE_TAP_MS = 400;

/** The last group key press or row click: which group, and when (ms). */
export interface GroupTap {
  number: number;
  time: number;
}

/** Whether a tap on group `number` at `now` (ms) follows `last` on the same group closely enough to pan. */
export function isDoubleTap(last: GroupTap | null, number: number, now: number): boolean {
  return last !== null && last.number === number && now - last.time <= GROUP_DOUBLE_TAP_MS;
}

/** Whether `selected` is exactly the group's members (any order): pressing its key again goes there. */
export function isExactSelection(group: OwnGroup, selected: readonly string[]): boolean {
  const ids = new Set(selected);
  return ids.size === group.units.length && group.units.every((u) => ids.has(u.id));
}

/** Where the camera goes for a group: its leader's tile, else its first member's. */
export function groupFocus(group: OwnGroup): Cell | null {
  const leader = group.units.find((u) => u.id === group.leader) ?? group.units[0];
  return leader ? leader.pos : null;
}
